import { Grid2x2, Home, LayoutGrid, MessageCircle, PlusSquare, UserCircle2, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { getAuthState, logoutUser } from "@/lib/authSession";
import { categories } from "@/data/mockData";

const MobileBottomNav = () => {
  const location = useLocation();
  const auth = getAuthState();
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  const items = auth.loggedIn
    ? [
        { label: "Home", href: "/", icon: Home },
        { label: "Explore", href: "/browse", icon: LayoutGrid },
        { label: "Categories", icon: Grid2x2, action: () => setCategoryMenuOpen(true) },
        { label: "Chat", href: "/dashboard/messages", icon: MessageCircle },
        { label: "Account", href: "/dashboard", icon: UserCircle2 },
      ]
    : [
        { label: "Home", href: "/", icon: Home },
        { label: "Explore", href: "/browse", icon: LayoutGrid },
        { label: "Categories", icon: Grid2x2, action: () => setCategoryMenuOpen(true) },
        { label: "List", href: "/login", icon: PlusSquare },
        { label: "Login", href: "/login", icon: UserCircle2 },
      ];

  return (
    <>
      {categoryMenuOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 md:hidden" onClick={() => setCategoryMenuOpen(false)}>
          <div className="h-full w-[82%] max-w-xs rounded-r-3xl bg-card p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-foreground">Categories</h3>
              <button className="rounded-full bg-muted p-2" onClick={() => setCategoryMenuOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="space-y-2 overflow-y-auto">
              <Link to="/categories" className="block rounded-xl bg-primary px-3 py-3 text-sm font-semibold text-primary-foreground" onClick={() => setCategoryMenuOpen(false)}>
                View all categories
              </Link>
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/search?category=${encodeURIComponent(category.name)}`}
                  className="block rounded-xl border px-3 py-3 text-sm font-medium text-foreground"
                  onClick={() => setCategoryMenuOpen(false)}
                >
                  <span className="mr-2">{category.icon}</span>
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {accountMenuOpen && auth.loggedIn && (
        <div className="fixed inset-0 z-[60] bg-black/40 md:hidden" onClick={() => setAccountMenuOpen(false)}>
          <div className="absolute bottom-24 right-3 w-52 rounded-2xl border bg-card p-2 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center gap-2 rounded-xl bg-muted p-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                {auth.user.slice(0, 1).toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-semibold capitalize text-foreground">{auth.user}</p>
                <p className="text-[10px] uppercase text-muted-foreground">{auth.role}</p>
              </div>
            </div>
            <Link to="/dashboard" className="block rounded-lg px-3 py-2 text-sm hover:bg-accent" onClick={() => setAccountMenuOpen(false)}>Dashboard</Link>
            <Link to="/dashboard/ads" className="block rounded-lg px-3 py-2 text-sm hover:bg-accent" onClick={() => setAccountMenuOpen(false)}>Ad Manager</Link>
            <Link to="/premium" className="block rounded-lg px-3 py-2 text-sm hover:bg-accent" onClick={() => setAccountMenuOpen(false)}>Verification</Link>
            {auth.role === "admin" && <Link to="/admin" className="block rounded-lg px-3 py-2 text-sm hover:bg-accent" onClick={() => setAccountMenuOpen(false)}>Admin Panel</Link>}
            <button className="mt-1 w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-accent" onClick={() => { logoutUser(); window.location.href = "/"; }}>Logout</button>
          </div>
        </div>
      )}

      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 px-3 pb-3 md:hidden">
        <nav className="pointer-events-auto mx-auto flex max-w-md items-center justify-between rounded-[1.4rem] border border-border/80 bg-card/95 px-2 py-2 shadow-2xl backdrop-blur-xl">
          {items.map((item) => {
            const Icon = item.icon;
            const active = item.href ? location.pathname === item.href || (item.href !== "/" && location.pathname.startsWith(item.href)) : false;

            if (item.label === "Account" && auth.loggedIn) {
              return (
                <button
                  key={item.label}
                  onClick={() => setAccountMenuOpen(true)}
                  className="flex min-w-[60px] flex-col items-center gap-1 rounded-2xl px-2 py-2 text-[10px] font-semibold text-muted-foreground transition-all"
                >
                  <div className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
                    {auth.user.slice(0, 1).toUpperCase()}
                  </div>
                  <span>{item.label}</span>
                </button>
              );
            }

            if (item.action) {
              return (
                <button
                  key={item.label}
                  onClick={item.action}
                  className="flex min-w-[60px] flex-col items-center gap-1 rounded-2xl px-2 py-2 text-[10px] font-semibold text-muted-foreground transition-all"
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </button>
              );
            }

            return (
              <Link
                key={item.label}
                to={item.href!}
                className={cn(
                  "flex min-w-[60px] flex-col items-center gap-1 rounded-2xl px-2 py-2 text-[10px] font-semibold transition-all",
                  active ? "bg-primary text-primary-foreground" : "text-muted-foreground",
                )}
              >
                <Icon size={18} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>
    </>
  );
};

export default MobileBottomNav;
